import React, { useState, useEffect } from 'react';
import { Modal, Grid, Stack, Title, Text, Badge, Divider, ScrollArea, Flex, Card, Button, Loader } from '@mantine/core';
import { Row, Col, Label, Input, Modal as RSModal, ModalHeader, ModalBody } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getBreachStatusList } from '../../../../slices/breachStatus/thunk';
import { getAuthorityStatusList } from '../../../../slices/authorityStatus/thunk';
import { getFile, downloadFile } from '../../../../slices/fileManager/thunk';
import { updateBreachRecordApi } from '../../../../helpers/breach_record/api_helper';
import { uploadFiles } from '../../../../helpers/file_manager/backend_helper';
import { toast } from 'react-toastify';

const getUserDisplayName = user =>
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
    user?.name ||
    user?.username ||
    '';

const getImpactoBadgeColor = label => {
    const l = (label || '').toLowerCase();
    if (l.includes('alto')) return 'red';
    if (l.includes('medio')) return 'yellow';
    if (l.includes('bajo')) return 'teal';
    return 'gray';
};

const getRiesgoBadgeColor = label => {
    const l = (label || '').toLowerCase();
	if (l.includes('alto')) return 'red';
	if (l.includes('medio')) return 'yellow';
	if (l.includes('bajo')) return 'green';
    return 'gray';
};

const getStatusBadgeColor = label => {
    const l = (label || '').toLowerCase();
    if (l.includes('no cumple')) return 'red';
    if (l.includes('parcialmente')) return 'yellow';
    if (l.includes('cumple')) return 'green';
    if (l.includes('pendiente')) return 'yellow';
    return 'gray';
};

const getAuthorityStatusBadgeColor = label => {
    const l = (label || '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
    if (l.includes('no cumple')) return 'red';
    if (l.includes('parcialmente')) return 'yellow';
    if (l.includes('revision')) return 'blue';
    if (l.includes('cumple')) return 'green';
    if (l.includes('pendiente')) return 'gray';
    return 'gray';
};

const normalizeFileIds = fileValue => {
    const rawValues = Array.isArray(fileValue)
        ? fileValue
        : fileValue
            ? [fileValue]
            : [];

    return rawValues
        .map(item => {
            if (!item) return null;
            if (typeof item === 'string') return item;
            return item._id || item.id || null;
        })
        .filter(Boolean);
};

const ViewDetallesIncumplimiento = ({ dataActividad, isOpen, handleToggleModal, onStatusChange, statusMode = 'breach' }) => {
    const dispatch = useDispatch();
    const { breachStatusListSelect } = useSelector(state => state.BreachStatus);
    const { authorityStatusListSelect } = useSelector(state => state.AuthorityStatus);
    const [isUpdating, setIsUpdating] = useState(false);
    const [evidenceFiles, setEvidenceFiles] = useState([]);
    const [fileLoading, setFileLoading] = useState(false);
    const [downloadingFileId, setDownloadingFileId] = useState(null);
    const [previewingFileId, setPreviewingFileId] = useState(null);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [selectedClosureFile, setSelectedClosureFile] = useState(null);

    useEffect(() => {
        if (isOpen) {
            dispatch(getBreachStatusList());
            dispatch(getAuthorityStatusList());
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !dataActividad) {
            setEvidenceFiles([]);
            return;
        }

        if (Array.isArray(dataActividad.file_data) && dataActividad.file_data.length > 0) {
            setEvidenceFiles(
                dataActividad.file_data
                    .filter(Boolean)
                    .map(file => ({ ...file, _id: file?._id || file?.id }))
            );
            return;
        }

        const fileIds = normalizeFileIds(dataActividad.file_id);
        if (fileIds.length === 0) {
            setEvidenceFiles([]);
            return;
        }

        setFileLoading(true);
        Promise.all(
            fileIds.map(fileId =>
                dispatch(getFile(fileId))
                    .unwrap()
                    .then(file => ({ ...file, _id: file?._id || file?.id || fileId }))
            )
        )
            .then(files => setEvidenceFiles(files.filter(Boolean)))
            .catch(() => setEvidenceFiles([]))
            .finally(() => setFileLoading(false));
    }, [isOpen, dataActividad, dispatch]);

    const handleDownloadFile = async (fileObj, fileId) => {
        const resolvedFileId = fileId || fileObj?._id || fileObj?.id;
        if (!resolvedFileId || downloadingFileId === resolvedFileId) return;
        setDownloadingFileId(resolvedFileId);
        try {
            const blob = await dispatch(downloadFile({ file_id: resolvedFileId })).unwrap();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileObj?.filename || 'archivo';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } finally {
            setDownloadingFileId(null);
        }
    };

    const handlePreviewClick = async (fileObj, fileId) => {
        const resolvedFileObj = fileObj;
        const resolvedFileId = fileId || fileObj?._id || fileObj?.id;
        if (!resolvedFileId || previewingFileId) return;
        setPreviewingFileId(resolvedFileId);
        try {
            const blob = await dispatch(downloadFile({ file_id: resolvedFileId, disable_msg: true })).unwrap();
            if (!(blob instanceof Blob)) throw new Error('El archivo no es válido.');
            const fileURL = URL.createObjectURL(blob);
            setPreviewFile({ ...resolvedFileObj, url: fileURL });
            setIsPreviewModalOpen(true);
        } catch (error) {
            console.error('Error al obtener el archivo:', error);
        } finally {
            setPreviewingFileId(null);
        }
    };

    const handleClosePreview = () => {
        if (previewFile?.url) URL.revokeObjectURL(previewFile.url);
        setPreviewFile(null);
        setIsPreviewModalOpen(false);
    };

    const statusList = statusMode === 'authority' ? authorityStatusListSelect : breachStatusListSelect;

    const findStatusId = name =>
        statusList?.find(s => s.label?.toLowerCase() === name.toLowerCase())?.value || null;

    const handleStatusChange = async statusId => {
        if (!statusId || !dataActividad?._id) return;
        const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
        const modificationUser =
            authUser?.username || authUser?.email || authUser?._id || authUser?.id || 'system';
        setIsUpdating(true);
        try {
            let closureFileId = null;
            if (selectedClosureFile) {
                const formData = new FormData();
                formData.append('files', selectedClosureFile);
                const uploadResponse = await uploadFiles({ formData });
                if (!uploadResponse || !uploadResponse.length || !uploadResponse[0]._id) {
                    toast.error('Error al subir el respaldo de cierre');
                    return;
                }
                closureFileId = uploadResponse[0]._id;
            }

            const updateField = statusMode === 'authority' ? 'authority_status_id' : 'breach_status_id';
            await updateBreachRecordApi({
                _id: dataActividad._id,
                [updateField]: statusId,
                modification_user: modificationUser,
                ...(closureFileId ? { finding_closure_file_id: closureFileId } : {}),
            });
            onStatusChange?.();
            handleToggleModal();
        } finally {
            setIsUpdating(false);
        }
    };

    if (!dataActividad) return null;

    const normalizeStr = s => (s || '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
    const activityStatusName = normalizeStr(dataActividad?.activity_status?.name);
    const canChangeStatus = statusMode === 'authority' ? true : activityStatusName !== 'pendiente';

    const riesgoLabel = dataActividad?.breach_risk_classification?.name || '';
    const statusLabel = dataActividad?.breach_status?.name || '';
    const authorityStatusLabel = dataActividad?.authority_status?.name || '';
    const breachDate = dataActividad?.breach_date
        ? new Date(dataActividad.breach_date).toLocaleDateString('es-CL')
        : 'No especificada';

    return (
        <>
        <Modal
            opened={isOpen}
            onClose={handleToggleModal}
            size='80%'
            padding='lg'
            centered
            radius={8}
            transitionProps={{ transition: 'fade', duration: 200 }}
            scrollAreaComponent={ScrollArea.Autosize}
            title={
                <Text fw={600} size='lg'>
                    Detalle del Incumplimiento{statusMode === 'authority' ? ' — Respuesta Autoridad' : ''}
                </Text>
            }
        >
            <Flex gap='md'>
                {/* Left column */}
                <Card style={{ flex: 2 }}>
                    <Card.Section inheritPadding mt='sm'>
                        <Row>
                            <Col lg={12} className='mb-3'>
                                <Label className='form-label'>Descripción</Label>
                                <Input
                                    type='textarea'
                                    rows='3'
                                    value={dataActividad?.description || ''}
                                    disabled
                                    readOnly
                                />
                            </Col>

                            <Col lg={12} className='mb-3'>
                                <Label className='form-label'>Medida Correctiva</Label>
                                <Input
                                    type='textarea'
                                    rows='3'
                                    value={dataActividad?.corrective_measure || ''}
                                    disabled
                                    readOnly
                                />
                            </Col>

                            <Col lg={12} className='mb-3'>
                                <Label className='form-label'>Observación</Label>
                                <Input
                                    type='textarea'
                                    rows='3'
                                    value={dataActividad?.observation || ''}
                                    placeholder='No hay observación'
                                    disabled
                                    readOnly
                                />
                            </Col>

                            <Col lg={12} className='mb-3'>
                                <Label className='form-label'>Archivo Adjunto</Label>
                                {fileLoading ? (
                                    <Flex justify='center' py='sm'><Loader size='sm' /></Flex>
                                ) : evidenceFiles.length > 0 ? (
                                    <div className='d-flex flex-column gap-2'>
                                        {evidenceFiles.map(file => {
                                            const fileId = file._id || file.id;
                                            return (
                                                <div key={fileId} className='d-flex align-items-center gap-2 border rounded px-3 py-2'>
                                                    <i className='ri-file-line'></i>
                                                    <span className='flex-grow-1 small'>{file.filename}</span>
                                                    <Button
                                                        size='xs'
                                                        variant='light'
                                                        disabled={Boolean(downloadingFileId)}
                                                        style={{ width: 100 }}
                                                        onClick={() => handleDownloadFile(file, fileId)}
                                                    >
                                                        {downloadingFileId === fileId ? <Loader size='xs' /> : 'Descargar'}
                                                    </Button>
                                                    <Button
                                                        size='xs'
                                                        variant='light'
                                                        disabled={Boolean(previewingFileId)}
                                                        style={{ width: 100 }}
                                                        onClick={() => handlePreviewClick(file, fileId)}
                                                    >
                                                        {previewingFileId === fileId ? <Loader size='xs' /> : 'Previsualizar'}
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <Input
                                        type='text'
                                        value='No hay archivo adjunto'
                                        readOnly
                                    />
                                )}
                            </Col>

                            <Col lg={12} className='mb-3'>
                                <Label className='form-label'>Respaldo de Cierre</Label>
                                <Input
                                    id='finding_closure_document'
                                    className='form-control'
                                    disabled={isUpdating || !canChangeStatus}
                                    type='file'
                                    onChange={e => setSelectedClosureFile(e.target.files[0] || null)}
                                />
                                {selectedClosureFile && (
                                    <small className='text-muted'>Archivo seleccionado: {selectedClosureFile.name}</small>
                                )}
                            </Col>
                        </Row>
                    </Card.Section>
                </Card>

                {/* Right column */}
                <Card style={{ flex: 1 }} mt='md'>
                    <Card.Section inheritPadding>
                        <Title order={5} mb='sm' c='dark'>Características del Incumplimiento</Title>

                        <Flex justify='space-between' align='center' py='xs'>
                            <Text size='sm' c='dimmed'>Fecha</Text>
                            <Flex align='center' gap='xs'>
                                <i className='icon-calendar' style={{ fontSize: '14px', color: '#666' }}></i>
                                <Text size='sm'>{breachDate}</Text>
                            </Flex>
                        </Flex>

                        <Flex justify='space-between' align='center' py='xs'>
                            <Text size='sm' c='dimmed'>Tipo Fiscalización</Text>
                            <Flex align='center' gap='xs'>
                                <i className='icon-clipboard-check' style={{ fontSize: '14px', color: '#666' }}></i>
                                <Text size='sm'>{dataActividad?.inspection_types?.name || 'No especificado'}</Text>
                            </Flex>
                        </Flex>

                        <Flex justify='space-between' align='center' py='xs'>
                        <Text size='sm' c='dimmed'>Entidad(es) Fiscalizadora(s)</Text>
                        <Flex align='center' gap='xs'>
                            <i className='icon-shield' style={{ fontSize: '14px', color: '#666' }}></i>
                            <Text size='sm'>
                                {Array.isArray(dataActividad?.inspection_authorities) && dataActividad.inspection_authorities.length > 0
                                    ? dataActividad.inspection_authorities.map(a => a.name).filter(Boolean).join(', ')
                                    : dataActividad?.inspection_authority?.name || 'No especificada'}
                            </Text>
                        </Flex>
                    </Flex>

                    <Flex justify='space-between' align='center' py='xs'>
                        <Text size='sm' c='dimmed'>Área Responsable</Text>
                        <Flex align='center' gap='xs'>
                            <i className='icon-users' style={{ fontSize: '14px', color: '#666' }}></i>
                            <Text size='sm'>{dataActividad?.breach_responsible_area?.name || 'No especificada'}</Text>
                        </Flex>
                    </Flex>

                    <Flex justify='space-between' align='center' py='xs'>
                        <Text size='sm' c='dimmed'>Responsable</Text>
                        <Flex align='center' gap='xs'>
                            <i className='icon-user' style={{ fontSize: '14px', color: '#666' }}></i>
                            <Text size='sm'>
                                {Array.isArray(dataActividad?.responsible_users) && dataActividad.responsible_users.length > 0
                                    ? dataActividad.responsible_users.map(getUserDisplayName).filter(Boolean).join(', ')
                                    : getUserDisplayName(dataActividad?.responsible_user) || 'No asignado'}
                            </Text>
                        </Flex>
                    </Flex>

                        <Flex justify='space-between' align='center' py='xs'>
                            <Text size='sm' c='dimmed'>Categoría</Text>
                            <Flex align='center' gap='xs'>
                                <i className='icon-tag' style={{ fontSize: '14px', color: '#666' }}></i>
                                <Text size='sm'>{dataActividad?.breach_category?.name || 'No especificada'}</Text>
                            </Flex>
                        </Flex>

                        <Flex justify='space-between' align='center' py='xs'>
                            <Text size='sm' c='dimmed'>Tipo de Evidencia</Text>
                            <Flex align='center' gap='xs'>
                                <i className='icon-clipboard' style={{ fontSize: '14px', color: '#666' }}></i>
                                <Text size='sm'>
                                    {Array.isArray(dataActividad?.breach_type_of_evidences) && dataActividad.breach_type_of_evidences.length > 0
                                        ? dataActividad.breach_type_of_evidences.map(e => e.name).filter(Boolean).join(', ')
                                        : dataActividad?.breach_type_of_evidence?.name || 'No especificado'}
                                </Text>
                            </Flex>
                        </Flex>

                        <Divider my='md' />

                        <Title order={5} mb='sm' c='dark'>Información Adicional</Title>

                        <Flex justify='space-between' align='center' py='xs'>
                            <Text size='sm' c='dimmed'>Estado</Text>
                            <Flex align='center' gap='xs'>
                                <i className='icon-check-circle' style={{ fontSize: '14px', color: '#666' }}></i>
                                {statusLabel ? (
                                    <Badge color={getStatusBadgeColor(statusLabel)} variant='light' size='sm' tt='capitalize'>
                                        {statusLabel}
                                    </Badge>
                                ) : (
                                    <Text size='sm' c='dimmed'>No especificado</Text>
                                )}
                            </Flex>
                        </Flex>

                        <Flex justify='space-between' align='center' py='xs'>
                            <Text size='sm' c='dimmed'>Respuesta Autoridad</Text>
                            <Flex align='center' gap='xs'>
                                <i className='icon-shield' style={{ fontSize: '14px', color: '#666' }}></i>
                                {authorityStatusLabel ? (
                                    <Badge color={getAuthorityStatusBadgeColor(authorityStatusLabel)} variant='light' size='sm' tt='capitalize'>
                                        {authorityStatusLabel}
                                    </Badge>
                                ) : (
                                    <Text size='sm' c='dimmed'>No especificado</Text>
                                )}
                            </Flex>
                        </Flex>

                        <Flex justify='space-between' align='center' py='xs'>
                            <Text size='sm' c='dimmed'>Origen</Text>
                            <Flex align='center' gap='xs'>
                                <Text size='sm'>{dataActividad?.breach_origin?.name || 'No especificado'}</Text>
                            </Flex>
                        </Flex>

                        <Flex justify='space-between' align='center' py='xs'>
                            <Text size='sm' c='dimmed'>Riesgo</Text>
                            <Flex align='center' gap='xs'>
                                <i className='icon-alert-triangle' style={{ fontSize: '14px', color: '#d32f2f' }}></i>
                                {riesgoLabel ? (
                                    <Badge color={getImpactoBadgeColor(riesgoLabel)} variant='light' size='xs' tt={'capitalize'}>
                                        {riesgoLabel}
                                    </Badge>
                                ) : (
                                    <Text size='sm' c='dimmed'>No especificado</Text>
                                )}
                            </Flex>
                        </Flex>

                        <Divider my='md' />

                        <Flex gap='xs' justify='center' wrap='wrap'>
                            <Button
                                size='sm'
                                color='red'
                                variant={(statusMode === 'authority' ? authorityStatusLabel : statusLabel).toLowerCase() === 'no cumple' ? 'filled' : 'light'}
                                disabled={isUpdating || !canChangeStatus}
                                onClick={() => handleStatusChange(findStatusId('No Cumple'))}
                            >
                                No Cumple
                            </Button>
                            <Button
                                size='sm'
                                color='yellow'
                                variant={(statusMode === 'authority' ? authorityStatusLabel : statusLabel).toLowerCase().includes('parcialmente') ? 'filled' : 'light'}
                                disabled={isUpdating || !canChangeStatus}
                                onClick={() => handleStatusChange(findStatusId('Cumple Parcialmente'))}
                            >
                                Cumple Parcialmente
                            </Button>
                            <Button
                                size='sm'
                                color='green'
                                variant={(statusMode === 'authority' ? authorityStatusLabel : statusLabel).toLowerCase() === 'cumple' ? 'filled' : 'light'}
                                disabled={isUpdating || !canChangeStatus}
                                onClick={() => handleStatusChange(findStatusId('Cumple'))}
                            >
                                Cumple
                            </Button>
                            {statusMode === 'authority' && (
                                <Button
                                    size='sm'
                                    color='blue'
                                    variant={authorityStatusLabel.normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase().includes('revision') ? 'filled' : 'light'}
                                    disabled={isUpdating}
                                    onClick={() => handleStatusChange(findStatusId('En revisión'))}
                                >
                                    En revisión
                                </Button>
                            )}
                        </Flex>

                    </Card.Section>
                </Card>
            </Flex>
        </Modal>

        <RSModal
                isOpen={isPreviewModalOpen}
                toggle={handleClosePreview}
                centered={false}
                className='w-100 p-4'
                fullscreen={true}
                scrollable={false}
            >
                <ModalHeader toggle={handleClosePreview}>
                    Previsualización
                </ModalHeader>
                <ModalBody className='overflow-hidden'>
                    {previewFile && previewFile.url ? (
                        previewFile.mime_type?.includes('image') ? (
                            <img
                                src={previewFile.url}
                                alt={previewFile.filename}
                                className='img-fluid'
                            />
                        ) : previewFile.mime_type?.includes('pdf') ? (
                            <iframe src={previewFile.url} width='100%' height='100%' title='preview' />
                        ) : previewFile.mime_type?.includes('text') ? (
                            <pre>{previewFile.content || 'No se pudo cargar el contenido'}</pre>
                        ) : (
                            <p>Formato no compatible para previsualización.</p>
                        )
                    ) : (
                        <p>Cargando...</p>
                    )}
                </ModalBody>
            </RSModal>
        </>
    );
};

export default ViewDetallesIncumplimiento;
